import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Target, Users, Rocket, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const AboutSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [100, -100]);

  const valueIcons = [Target, Users, Rocket, Shield];
  const values = t('about.values');

  const textRevealVariants = {
    hidden: { opacity: 0, y: 80 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    })
  };

  const techStack = ['React', 'Node.js', 'Python', 'AWS', 'TensorFlow', 'MongoDB', 'FastAPI', 'Docker'];

  return (
    <section 
      id="about" 
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#0A0A0B] relative overflow-hidden" 
      data-testid="about-section"
    >
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.span 
              custom={0}
              variants={textRevealVariants}
              className="label-tag text-[#FF4D2D] mb-4 block"
            >
              {t('about.label')}
            </motion.span>
            
            <div className="overflow-hidden">
              <motion.h2 
                custom={1}
                variants={textRevealVariants}
                className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-[#FAFAFA] mb-2"
              >
                {t('about.title1')}
              </motion.h2>
            </div>
            <div className="overflow-hidden mb-8">
              <motion.h2 
                custom={2}
                variants={textRevealVariants}
                className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-[#FF4D2D]"
              >
                {t('about.title2')}
              </motion.h2>
            </div>

            <motion.div 
              custom={3}
              variants={textRevealVariants}
              className="space-y-4 text-[#A1A1AA] text-lg leading-relaxed"
            >
              <p>{t('about.p1')}</p>
              <p>{t('about.p2')}</p>
              <p>{t('about.p3')}</p>
            </motion.div>

            {/* Key Numbers */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="grid grid-cols-3 gap-8 mt-12 pt-12 border-t border-[#27272A]"
            >
              {[
                { number: '5+', label: t('about.stat1') },
                { number: '50+', label: t('about.stat2') },
                { number: '∞', label: t('about.stat3') },
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 + (index * 0.1), type: "spring", stiffness: 200 }}
                  whileHover={{ scale: 1.1, y: -5 }}
                >
                  <div className="text-3xl md:text-4xl font-bold text-[#FF4D2D]">
                    {stat.number}
                  </div>
                  <div className="text-sm text-[#A1A1AA] mt-1">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Values Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 gap-6"
          >
            {values.map((value, index) => {
              const IconComponent = valueIcons[index];
              const isHighlighted = index === 0 || index === 3;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ 
                    delay: 0.2 + (index * 0.15), 
                    duration: 0.6,
                    ease: "easeOut"
                  }}
                  whileHover={{ 
                    scale: 1.03, 
                    y: -8,
                  }}
                  className={`rounded-2xl p-6 group transition-all duration-300 cursor-pointer border ${
                    isHighlighted 
                      ? 'bg-[#E03A1C] border-[#E03A1C]' 
                      : 'bg-[#18181B] border-[#27272A] hover:border-[#FF4D2D]/30'
                  }`}
                  data-testid={`value-card-${index}`}
                >
                  <motion.div 
                    className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300 ${
                      isHighlighted 
                        ? 'bg-[#FAFAFA]/20' 
                        : 'bg-[#FF4D2D]/10 group-hover:bg-[#FF4D2D]/20'
                    }`}
                    whileHover={{ rotate: 10, scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <IconComponent className={`w-6 h-6 ${isHighlighted ? 'text-[#FAFAFA]' : 'text-[#FF4D2D]'}`} />
                  </motion.div>
                  <h3 className={`text-xl font-semibold mb-2 tracking-tight ${
                    isHighlighted ? 'text-[#FAFAFA]' : 'text-[#FAFAFA]'
                  }`}>
                    {value.title}
                  </h3>
                  <p className={`text-sm leading-relaxed ${
                    isHighlighted ? 'text-[#FAFAFA]/80' : 'text-[#A1A1AA]'
                  }`}>
                    {value.description}
                  </p>
                  <motion.div 
                    className={`w-0 h-0.5 mt-4 group-hover:w-full transition-all duration-500 ${
                      isHighlighted ? 'bg-[#FAFAFA]' : 'bg-[#FF4D2D]'
                    }`}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Tech Stack */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-24 pt-16 border-t border-[#27272A]"
        >
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center label-tag text-[#A1A1AA] mb-8"
          >
            {t('about.techLabel')}
          </motion.p>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {techStack.map((tech, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ 
                  delay: 0.4 + (index * 0.08),
                  type: "spring",
                  stiffness: 200
                }}
                whileHover={{ 
                  scale: 1.1, 
                  y: -3,
                  backgroundColor: "rgba(255, 77, 45, 0.1)",
                  borderColor: "rgba(255, 77, 45, 0.5)"
                }}
                className="px-5 py-2.5 border border-[#27272A] rounded-full text-[#A1A1AA] hover:text-[#FF4D2D] transition-all duration-300 cursor-default text-sm"
              >
                {tech}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
